import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconX,
} from '@tabler/icons-react';

const FONT = '"Public Sans", sans-serif';

const ALERT_TYPES = {
  success: {
    color: '#30d158',
    tint: 'rgba(48, 209, 88, 0.14)',
    icon: IconCircleCheckFilled,
    autoClose: true,
  },
  error: {
    color: '#ff453a',
    tint: 'rgba(255, 69, 58, 0.14)',
    icon: IconAlertTriangleFilled,
    autoClose: false,
  },
  loading: {
    color: '#0a84ff',
    tint: 'rgba(10, 132, 255, 0.14)',
    icon: null,
    autoClose: false,
  },
};

// Versión de bolsillo de la DynamicIsland: en el celular no hay pastilla
// permanente ocupando la pantalla — solo aparece cuando hay algo que decir
// y se retira sola (éxito) o con un toque (error).
const DynamicIslandMobile = forwardRef(function DynamicIslandMobile(_, ref) {
  const [alerts, setAlerts] = useState([]);

  const hasAlerts = alerts.length > 0;
  const currentAlert = alerts[alerts.length - 1];
  const alertType = currentAlert ? ALERT_TYPES[currentAlert.type] : null;
  const AlertIcon = alertType?.icon;
  const message = currentAlert?.message?.trim();
  const canDismiss = hasAlerts && currentAlert.type !== 'loading';

  useImperativeHandle(ref, () => ({
    addAlert: (type, message) => {
      const newAlert = { id: Date.now(), type, message };
      setAlerts((prev) => [...prev, newAlert]);
    },
    clearAlerts: () => {
      setAlerts([]);
    },
  }));

  useEffect(() => {
    if (!hasAlerts || !alertType || !alertType.autoClose) return;
    const timer = setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== currentAlert.id));
    }, 3000);
    return () => clearTimeout(timer);
  }, [hasAlerts, alertType, currentAlert, alerts.length]);

  if (!hasAlerts) return null;

  const handleClick = () => {
    if (!canDismiss) return;
    setAlerts((prev) => prev.filter((a) => a.id !== currentAlert.id));
  };

  return (
    <div
      role={currentAlert.type === 'error' ? 'alert' : 'status'}
      aria-live={currentAlert.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      title={message}
      tabIndex={canDismiss ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={
        canDismiss
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      className={`flex items-center gap-2.5 h-11 pl-1.5 pr-4 overflow-hidden select-none ${
        canDismiss ? 'cursor-pointer active:scale-[0.98]' : 'cursor-default'
      }`}
      style={{
        backgroundColor: 'rgba(18, 18, 18, 0.94)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '9999px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        fontFamily: FONT,
        maxWidth: 'calc(100vw - 32px)',
        animation: 'mobileIslandIn 320ms cubic-bezier(0.32, 0.72, 0, 1)',
        transition: 'transform 200ms ease-out',
      }}
    >
      <span
        className="flex items-center justify-center shrink-0 w-8 h-8 rounded-full"
        style={{ backgroundColor: alertType.tint }}
      >
        {currentAlert.type === 'loading' ? (
          <span
            aria-hidden="true"
            style={{
              width: '15px',
              height: '15px',
              border: '2px solid rgba(255, 255, 255, 0.22)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'mobileIslandSpin 0.8s linear infinite',
            }}
          />
        ) : (
          AlertIcon && <AlertIcon size={17} color={alertType.color} />
        )}
      </span>

      <span
        className="min-w-0 truncate font-semibold"
        style={{
          fontSize: '14px',
          lineHeight: '18px',
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        {currentAlert.type === 'loading' ? message || 'Procesando…' : message}
      </span>

      {canDismiss && (
        <IconX size={16} color="rgba(255, 255, 255, 0.4)" className="shrink-0" />
      )}

      <style>{`
        @keyframes mobileIslandSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes mobileIslandIn {
          from { opacity: 0; transform: translateY(-10px) scale(0.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
});

export default DynamicIslandMobile;
